import { FilterFilled } from "@ant-design/icons";
import { Checkbox, DatePicker, Switch } from "antd";
import {
  TASK_TRIGGER_ON_EVENT,
  TASK_TRIGGER_PERIODICALLY,
  TaskHubItem,
  TaskTrigger,
} from "shared/api";
import { useTranslation } from "react-i18next";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  DatePickerValue,
  MultipleOptionsTableFilter,
  RangeSlider,
  TableButton,
  TableSearchFilter,
} from "shared/ui";
import classNames from "classnames";

interface UseTaskHubColumnsProps {
  onEdit: (task: number | null) => void;
  onDelete: (id: number | null) => void;
  onDuplicate: (task: TaskHubItem) => Promise<void>;
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  handleToggleStatus: (task: TaskHubItem) => Promise<void>;
  handleTaskNameValue: (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  taskNameValue: string;
  handlePeriodicityValue: (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  priorityFilters: {
    minValue: string | number | undefined;
    maxValue: string | number | undefined;
    onChange: (value: [number, number]) => void;
    onAfterChange: (value: [number, number]) => void;
    reset: (min?: string | number, max?: string | number) => void;
    isFilterActive: () => boolean;
  };
  periodicityValue: string;
  enabledFilter: boolean | undefined;
  setEnabledFilter: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  handleStartDateGreater: (date: DatePickerValue, dateString: string) => void;
  handleStartDateLesser: (date: DatePickerValue, dateString: string) => void;
  startDateGreater: string | null;
  startDateLesser: string | null;
  isStartFilterActive: boolean;
  userIds: number[];
  setUserIds: React.Dispatch<React.SetStateAction<number[]>>;
  triggerInFilter: TaskTrigger[];
  setTriggerInFilter: React.Dispatch<React.SetStateAction<TaskTrigger[]>>;
  refetch: () => void;
}

export const useTaskHubColumns = ({
  onEdit,
  onDelete,
  onDuplicate,
  transformedUsers,
  handleToggleStatus,
  handleTaskNameValue,
  taskNameValue,
  handlePeriodicityValue,
  periodicityValue,
  enabledFilter,
  setEnabledFilter,
  handleStartDateGreater,
  handleStartDateLesser,
  isStartFilterActive,
  startDateGreater,
  startDateLesser,
  priorityFilters,
  setUserIds,
  userIds,
  triggerInFilter,
  setTriggerInFilter,
  refetch,
}: UseTaskHubColumnsProps): ColumnsType<TaskHubItem> => {
  const { t } = useTranslation();

  const timezone = localStorage.getItem("timezone") || "UTC";

  const getEnabledFilterString = (val: boolean | undefined) => {
    if (val === true) return "true";
    if (val === false) return "false";
    return "";
  };

  return [
    {
      title: t("taskHub.table.icon") || "",
      dataIndex: "icon",
      key: "icon",
      render: (icon: string | null) =>
        icon ? (
          <img
            src={icon}
            alt="icon"
            style={{ width: 24, height: 24, objectFit: "contain" }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: t("taskHub.table.name") || "",
      dataIndex: "name",
      key: "name",
      filterIcon: (
        <FilterFilled
          className={
            taskNameValue.length ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={() => handleTaskNameValue("")}
          confirm={confirm}
          value={taskNameValue}
          handleFilterChange={handleTaskNameValue}
          visible={visible}
          placeholder={t("taskHub.searchByName") || "Search by name"}
          title={t("Name") || ""}
        />
      ),
    },
    {
      title: t("taskHub.table.users") || "",
      dataIndex: "users",
      key: "users",
      render: (users: TaskHubItem["users"]) => {
        if (!Array.isArray(users) || users.length === 0) {
          return <span>N/A</span>;
        }

        const labels = users.map((u) => u.user?.username).filter(Boolean);

        return <span>{labels.join(", ")}</span>;
      },
      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[240px]">
          <Checkbox.Group
            className="flex flex-col items-start space-y-1 max-h-[200px] overflow-x-hidden overflow-y-auto"
            value={userIds}
            onChange={(checkedValues) => {
              setUserIds(checkedValues as number[]);
              confirm();
            }}
          >
            {transformedUsers.map((user) => (
              <Checkbox key={user.value} value={user.value}>
                {user.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            userIds.length > 0 ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.periodicity") || "",
      dataIndex: "periodicity",
      key: "periodicity",
      render: (periodicity: string) => {
        if (!periodicity) return "-";
        return (
          <span>
            {t(`taskHub.modal.${periodicity.toLowerCase()}`) || periodicity}
          </span>
        );
      },
      filterDropdown: ({ clearFilters }) => (
        <MultipleOptionsTableFilter
          options={[
            { label: t("taskHub.modal.daily") || "Daily", value: "daily" },
            { label: t("taskHub.modal.weekly") || "Weekly", value: "weekly" },
            {
              label: t("taskHub.modal.monthly") || "Monthly",
              value: "monthly",
            },
            { label: t("taskHub.modal.yearly") || "Yearly", value: "yearly" },
          ]}
          selectedValues={periodicityValue ? [periodicityValue] : []}
          handleChange={([value]) => {
            const val = String(value || "");
            handlePeriodicityValue(periodicityValue === val ? "" : val);
          }}
          clearFilters={clearFilters}
        />
      ),
      filteredValue: Array.isArray(periodicityValue) ? periodicityValue : [],
      filterIcon: (
        <FilterFilled
          className={
            Array.isArray(periodicityValue) && periodicityValue.length > 0
              ? "!text-blue-600 scale-125"
              : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.trigger") || "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      render: (trigger: TaskHubItem["trigger"]) => {
        if (!trigger) return "-";

        return <span>{trigger}</span>;
      },
      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[240px]">
          <Checkbox.Group
            className="flex flex-col items-start space-y-1"
            value={triggerInFilter}
            onChange={(checkedValues) => {
              setTriggerInFilter(checkedValues as TaskTrigger[]);
              confirm();
            }}
          >
            <Checkbox value={TASK_TRIGGER_ON_EVENT}>
              {TASK_TRIGGER_ON_EVENT}
            </Checkbox>
            <Checkbox className="!ml-0" value={TASK_TRIGGER_PERIODICALLY}>
              {TASK_TRIGGER_PERIODICALLY}
            </Checkbox>
          </Checkbox.Group>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            triggerInFilter.length > 0 ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.priority") || "",
      dataIndex: "priority",
      key: "priority",
      filterDropdown: () => (
        <RangeSlider
          title="Priority"
          step={1}
          maxValue={5}
          onChange={priorityFilters.onChange}
          onAfterChange={priorityFilters.onAfterChange}
          rangeItemName="Priority"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-yellow-600 scale-125": priorityFilters.isFilterActive(),
          })}
        />
      ),
      render: (priority: number) => <span>{priority}</span>,
    },

    {
      title: t("taskHub.table.status") || "",
      key: "enabled",
      dataIndex: "enabled",
      render: (status, task) => (
        <Switch
          style={{
            backgroundColor: status ? "limegreen" : "tomato",
          }}
          checked={task.enabled}
          onChange={() => handleToggleStatus(task)}
        />
      ),
      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[180px]">
          <div className="mb-1">{t("Filter by status")}</div>
          <select
            className="w-full border px-2 py-1 rounded"
            value={getEnabledFilterString(enabledFilter)}
            onChange={(e) => {
              const { value } = e.target;
              if (value === "true") setEnabledFilter(true);
              else if (value === "false") setEnabledFilter(false);
              else setEnabledFilter(undefined);
              confirm();
              refetch();
            }}
          >
            <option value="">{t("All")}</option>
            <option value="true">{t("Enabled")}</option>
            <option value="false">{t("Disabled")}</option>
          </select>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            enabledFilter !== undefined ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.start") || "",
      key: "start",
      sorter: (a, b) => {
        const dateA = a.start_periodicity
          ? dayjs(a.start_periodicity).tz(timezone).valueOf()
          : 0;
        const dateB = b.start_periodicity
          ? dayjs(b.start_periodicity).tz(timezone).valueOf()
          : 0;
        return dateB - dateA;
      },
      render: (task: TaskHubItem) => (
        <div className="text-sm">
          {task.start_periodicity
            ? dayjs(task.start_periodicity)
                .tz(timezone)
                .format("YYYY-MM-DD HH:mm")
            : "-"}
        </div>
      ),
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">{t("Start From")}</div>
          <DatePicker
            showTime
            value={startDateGreater ? dayjs(startDateGreater) : null}
            onChange={(val) =>
              handleStartDateGreater(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="mb-1 text-sm">{t("Start To")}</div>
          <DatePicker
            showTime
            value={startDateLesser ? dayjs(startDateLesser) : null}
            onChange={(val) =>
              handleStartDateLesser(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%" }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isStartFilterActive ? "!text-yellow-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.actions") || "",
      key: "actions",
      render: (task: TaskHubItem) => (
        <div className="flex gap-2">
          <TableButton
            type="edit"
            onClick={() => onEdit(task.id ? task.id : null)}
          />
          <TableButton
            popoverString="Duplicate Task"
            type="clone"
            onClick={() => onDuplicate(task)}
          />
          <TableButton
            type="delete"
            onClick={() => onDelete(task.id ? task.id : null)}
          />
        </div>
      ),
    },
  ];
};
